import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Radio from "@mui/material/Radio";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SyncIcon from "@mui/icons-material/Sync";
import { confirmAlert } from "react-confirm-alert";
import { useNavigate } from "react-router-dom";
import { createUpdate, createUpdatePorBimestre, createUpdatePorBimestreTurma } from "../api-aluno-frequencia";
import { findByAnoLetivo } from "../../Turma/api-turma";
import session from "../../../SessionHandler/session-helper";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { success } from "../../../util/ToastUtil";
import Container from "../../UI/Container";
import LoadingDialog from "../../UI/LoadingDialog";

const INSTITUICAO = "instituicao";
const BIMESTRE = "bimestre";
const BIMESTRE_TURMA = "bimestre-turma";

const ESCOPOS = [
  {
    value: INSTITUICAO,
    title: "Toda a instituição",
    description: "Todos os alunos e bimestres do ano letivo.",
  },
  {
    value: BIMESTRE,
    title: "Por bimestre",
    description: "Todos os alunos, apenas no bimestre escolhido.",
  },
  {
    value: BIMESTRE_TURMA,
    title: "Por bimestre e turma",
    description: "Apenas os alunos da turma, no bimestre escolhido.",
  },
];

const BIMESTRES = [1, 2, 3, 4];

export default function AlunoFrequenciaLoteContainer() {
  const navigate = useNavigate();
  const anoLetivo = session.getAnoLetivo();
  const [escopo, setEscopo] = useState(BIMESTRE_TURMA);
  const [bimestre, setBimestre] = useState(null);
  const [turma, setTurma] = useState(null);
  const [turmas, setTurmas] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    findByAnoLetivo().then((data) => {
      if (!data) return;
      if (data.error) {
        errorMessage(data.message);
      } else if (Array.isArray(data)) {
        setTurmas(data);
      }
    });
  }, []);

  const precisaBimestre = escopo !== INSTITUICAO;
  const precisaTurma = escopo === BIMESTRE_TURMA;
  const podeExecutar = (!precisaBimestre || bimestre) && (!precisaTurma || turma);

  const resumo = (() => {
    if (escopo === INSTITUICAO) return `todos os registros de frequência do ano letivo ${anoLetivo}`;
    const bim = bimestre ? `do ${bimestre}º bimestre` : "do bimestre";
    if (escopo === BIMESTRE) return `os registros de frequência ${bim} de todos os alunos`;
    return `os registros de frequência ${bim} da turma ${turma?.descricao ?? ""}`.trim();
  })();

  const request = () => {
    if (escopo === INSTITUICAO) return createUpdate();
    if (escopo === BIMESTRE) return createUpdatePorBimestre(bimestre);
    return createUpdatePorBimestreTurma(bimestre, turma.codigo);
  };

  const executar = () => {
    setIsProcessing(true);
    request()
      .then((data) => {
        if (!data) {
          errorMessage("Não foi possível concluir a operação. Tente novamente.");
          return;
        }
        if (data.error) {
          errorMessage(data.message);
          return;
        }
        success("Operação realizada com sucesso.");
      })
      .finally(() => setIsProcessing(false));
  };

  const handleExecutar = () => {
    confirmAlert({
      title: "Alerta",
      message: `Deseja criar/atualizar ${resumo}?`,
      buttons: [
        { label: "Sim", onClick: executar },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  return (
    <Container>
      <LoadingDialog
        openDialog={isProcessing}
        setOpenDialog={setIsProcessing}
        title="Aluno Frequência"
        description="Criando/Atualizando registros..."
      />

      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <Tooltip title="Voltar">
          <IconButton edge="start" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box>
          <Typography variant="h6">Aluno Frequência em Lote</Typography>
          <Typography variant="body2" color="text.secondary">
            Cria ou atualiza os registros de frequência dos alunos no ano letivo {anoLetivo}.
          </Typography>
        </Box>
      </Stack>

      <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: "uppercase" }} mb={1.5}>
        Abrangência
      </Typography>
      <Grid container spacing={2} mb={3}>
        {ESCOPOS.map((opcao) => {
          const selecionado = escopo === opcao.value;
          return (
            <Grid key={opcao.value} size={{ xs: 12, md: 4 }}>
              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  borderColor: selecionado ? "primary.main" : "divider",
                  borderWidth: selecionado ? 2 : 1,
                }}
              >
                <CardActionArea onClick={() => setEscopo(opcao.value)} sx={{ height: "100%", p: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Radio checked={selecionado} size="small" sx={{ p: 0, mt: 0.25 }} tabIndex={-1} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {opcao.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {opcao.description}
                      </Typography>
                    </Box>
                  </Stack>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {(precisaBimestre || precisaTurma) && (
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3} alignItems={{ md: "center" }}>
          {precisaBimestre && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={bimestre}
              onChange={(event, value) => value && setBimestre(value)}
              aria-label="Bimestre"
            >
              {BIMESTRES.map((b) => (
                <ToggleButton key={b} value={b}>
                  {b}º Bimestre
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          {precisaTurma && (
            <Autocomplete
              options={turmas}
              value={turma}
              onChange={(event, value) => setTurma(value)}
              getOptionLabel={(option) => option.descricao ?? ""}
              isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
              noOptionsText="Nenhuma turma encontrada"
              renderInput={(params) => <TextField {...params} size="small" label="Turma" />}
              sx={{ minWidth: 280 }}
            />
          )}
        </Stack>
      )}

      <Alert
        severity={escopo === INSTITUICAO ? "warning" : "info"}
        sx={{ mb: 2 }}
      >
        Serão criados/atualizados {resumo}.
        {escopo === INSTITUICAO && " Esta operação pode demorar."}
      </Alert>

      <Button variant="contained" startIcon={<SyncIcon />} disabled={!podeExecutar || isProcessing} onClick={handleExecutar}>
        Criar/Atualizar
      </Button>
    </Container>
  );
}
